import React, { useCallback, useMemo } from 'react';
import {
	Meta,
	useWorkplacesCsszSickLeaveQuery,
} from '../../../../../../graphql/graphql';
import MagicForm from '../../../../../../Components/Form/MagicForm';
import { ItemScheme } from '../../../../../../Components/Form/MagicFormItem';
import { MagicField } from '../../../../../../Enums/MagicField.enum';
import apiClient from '../../../../../../Services/ApiClient';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import useNotification from '../../../../../../Hooks/useNotification.hook';
import { SelectValue } from '../../../../../../Components/Base/Select/Select';
import useMeta from '../../../../../Meta/Hooks/useMeta';
import useLoading from '../../../../../Base/Hooks/useLoading';
import { TermType } from '../../../../../Term/Enums/TermType.enum';
import { MetaKey } from '../../../../../Meta/Enums/MetaKey.enum';

type ManagerHookUpCSSZSickLeaveFormItem = {
	add: boolean;
	districtCode: SelectValue;
};

type ManagerHookUpCSSZSickLeaveFormValues = Record<
	string,
	ManagerHookUpCSSZSickLeaveFormItem
>;

interface Props {
	onSave: () => void;
}

const ManagerHookUpCSSZSickLeave: React.FC<Props> = ({ onSave }) => {
	const { displayError } = useApiClient();
	const { getMetaAsObject } = useMeta();
	const { showNotificationAlert } = useNotification();
	const { isLoading, setIsLoading, RenderLoading } = useLoading(false);
	const {
		data,
		isLoading: isDataLoading,
		refetch,
	} = useWorkplacesCsszSickLeaveQuery();

	const workplaces = useMemo(
		() => data?.workplaces?.data?.filter(Boolean) ?? [],
		[data],
	);

	const initialState = useMemo(() => {
		const formValues: ManagerHookUpCSSZSickLeaveFormValues = {};
		const existingValues: Record<string, string | null> = {};

		workplaces.forEach(workplace => {
			const meta = getMetaAsObject((workplace.meta as Meta[]) ?? []);
			const sszCode = meta[MetaKey.CSSZ_SSZ_CODE];

			formValues[workplace.id] = {
				add: Boolean(sszCode),
				districtCode: sszCode,
			};

			existingValues[workplace.id] = sszCode ?? null;
		});

		return {
			formValues,
			existingValues,
		};
	}, [workplaces, getMetaAsObject]);

	const formScheme: ItemScheme[] = useMemo(() => {
		return workplaces.map(workplace => ({
			type: MagicField.CONTAINER,
			key: workplace.id,
			children: [
				{
					type: MagicField.CHECKBOX,
					key: 'add',
					title: workplace.name,
				},
				{
					type: MagicField.CONDITION,
					settings: {
						rules: [
							{
								formControlKey: `${workplace.id}.add`,
								rule: 'equal',
								value: true,
							},
						],
					},
					subType: 'view',
					children: [
						{
							type: MagicField.TERM_ITEM_ASYNC_SELECT,
							key: 'districtCode',
							title: 'component.hookUpWithCSSZSickLeaveComponent.districtCode',
							settings: {
								required: true,
								termType: TermType.SSZ_CODE,
							},
						},
					],
				},
			],
		}));
	}, [workplaces]);

	const handleSubmit = useCallback(
		async (values: ManagerHookUpCSSZSickLeaveFormValues) => {
			setIsLoading(true);

			try {
				const changedEntries = Object.entries(values).filter(
					([workplaceId, currentValue]) => {
						const originalDistrictCode =
							initialState.existingValues[workplaceId] ?? null;
						const nextDistrictCode = currentValue.add
							? currentValue.districtCode?.value ?? null
							: null;

						return originalDistrictCode !== nextDistrictCode;
					},
				);

				if (!changedEntries.length) {
					onSave();
					return;
				}

				const statuses = await Promise.all(
					changedEntries.map(async ([workplaceId, { add, districtCode }]) => {
						try {
							if (add) {
								const response = await apiClient.post(
									`/workplaces/${workplaceId}/meta`,
									{
										key: MetaKey.CSSZ_SSZ_CODE,
										value: districtCode,
									},
								);
								return response.status;
							}

							const response = await apiClient.delete(
								`/workplaces/${workplaceId}/meta/${MetaKey.CSSZ_SSZ_CODE}`,
							);
							return response.status;
						} catch (e) {
							displayError(e);
							return 500;
						}
					}),
				);

				if (statuses.every(status => status === 200)) {
					await refetch();
					onSave();
				} else {
					showNotificationAlert({ content: 'base.shared.warningBug' });
				}
			} finally {
				setIsLoading(false);
			}
		},
		[
			displayError,
			initialState.existingValues,
			onSave,
			refetch,
			setIsLoading,
			showNotificationAlert,
		],
	);

	if (isDataLoading || isLoading) {
		return <RenderLoading />;
	}

	return (
		<MagicForm
			formScheme={formScheme}
			defaultValues={initialState.formValues}
			onSubmit={handleSubmit}
		/>
	);
};

export default ManagerHookUpCSSZSickLeave;
